import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Demo term data
const DEMO_TERM = {
  id: '1',
  term: 'zeiv',
  translation: "aunt (father's older brother's wife)",
  partOfSpeech: 'NOUN',
  genderSpecific: true,
  generationLevel: 'all',
  lineageSpecific: 'PATERNAL',
  hasAudio: true,
  culturalNotes: 'Used to address the wife of one\'s father\'s older brother. This is an important term in Mien culture that shows respect for family hierarchy and the proper way to address relatives. The term distinguishes between the wife of the older brother versus the younger brother, reflecting the detailed nature of Mien kinship terminology.',
  examples: [
    { mien: 'Zeiv nyeemouv', english: 'Aunt, how are you?' },
    { mien: 'Zeiv hnam', english: 'Aunt is kind' },
    { mien: 'Nzung zeiv fin', english: 'I went to visit Aunt' },
  ],
  relatedTerms: [
    { id: '2', term: 'taaj', translation: 'grandfather (paternal)' },
    { id: '5', term: 'nyaaj', translation: 'grandmother (paternal)' },
  ],
  tags: ['kinship', 'paternal', 'female', 'elder'],
};

function CulturalTermDetailPage() {
  const { termId } = useParams();
  const navigate = useNavigate();
  const term = DEMO_TERM; // Will be replaced with GraphQL query
  const [isFavorite, setIsFavorite] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);

  const posLabels = { NOUN: 'Noun', VERB: 'Verb', ADJECTIVE: 'Adjective', PHRASE: 'Phrase', OTHER: 'Other' };
  const lineageLabels = { MATERNAL: 'Maternal', PATERNAL: 'Paternal', BOTH: 'Both' };

  const handlePlayAudio = () => {
    if (audioError) {
      setAudioError(false);
    }
    // TODO: implement actual audio playback
    setIsPlaying(!isPlaying);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/cultural-terms/${term.id}`;
    try {
      await navigator.clipboard.writeText(url);
      // Could show a toast here
    } catch {
      // Fallback
    }
  };

  return (
    <div className="">

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Term Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900">{term.term}</h2>
                  <p className="text-lg text-gray-600 mt-1">{term.translation}</p>
                </div>
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`ml-4 text-2xl transition-colors ${isFavorite ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}
                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isFavorite ? <AppIcon name="light" size={16} className="text-yellow-500" /> : <AppIcon name="light" size={16} className="text-gray-300" />}
                </button>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                  {posLabels[term.partOfSpeech] || term.partOfSpeech}
                </span>
                {term.genderSpecific && (
                  <span className="px-2.5 py-1 bg-pink-50 text-pink-700 rounded-lg text-sm font-medium">Gender-specific</span>
                )}
                {term.generationLevel && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-sm font-medium capitalize">
                    {term.generationLevel} generation
                  </span>
                )}
                {term.lineageSpecific && (
                  <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium">
                    {lineageLabels[term.lineageSpecific] || term.lineageSpecific}
                  </span>
                )}
              </div>
            </div>

            {/* Audio Player */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"><AppIcon name="pronunciation" size={14} /> Pronunciation</h3>
              {term.hasAudio ? (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handlePlayAudio}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isPlaying ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                    }`}
                    aria-label={isPlaying ? 'Pause' : 'Play pronunciation'}
                  >
                    {isPlaying ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 rounded-full" style={{ width: isPlaying ? '45%' : '0%' }} />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>{isPlaying ? '0:03' : '0:00'}</span>
                      <span>0:07</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No audio pronunciation available.</p>
              )}
              {audioError && (
                <p className="text-xs text-red-600 mt-2">Failed to load audio. <button onClick={handlePlayAudio} className="underline">Retry</button></p>
              )}
            </div>

            {/* Cultural Notes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"><AppIcon name="noun" size={14} /> Cultural Notes</h3>
              <p className="text-gray-700 leading-relaxed">{term.culturalNotes}</p>
            </div>

            {/* Examples */}
            {term.examples && term.examples.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"><AppIcon name="examples" size={14} /> Examples</h3>
                <div className="space-y-3">
                  {term.examples.map((ex, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{ex.mien}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{ex.english}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Terms */}
            {term.relatedTerms && term.relatedTerms.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"><AppIcon name="related" size={14} /> Related Terms</h3>
                <div className="space-y-2">
                  {term.relatedTerms.map((rt) => (
                    <a key={rt.id} href={`/cultural-terms/${rt.id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{rt.term}</p>
                        <p className="text-xs text-gray-500">{rt.translation}</p>
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2">
              <button
                onClick={() => navigate('/cultural-terms')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Back to Browse
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <AppIcon name="search" size={16} /> Random Term
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <span>⭐</span> View Favorites
              </button>
            </div>

            {/* Share */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Share</h4>
              <div className="space-y-2">
                <button
                  onClick={handleShare}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <AppIcon name="noun" size={16} /> Copy Link
                </button>
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  <AppIcon name="send" size={16} /> Share via...
                </button>
              </div>
            </div>

            {/* Tags */}
            {term.tags && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Tags</h4>
                <div className="flex flex-wrap gap-1.5">
                  {term.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">#{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Report */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <AppIcon name="bug" size={16} /> Report Issue
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CulturalTermDetailPage;